import { gql } from '@apollo/client';
import { fragments as physicianFragments } from '../physicians';
import { fragmentTransferIssue } from '../scheduleRequests';

const baseFields = `
  id
  status
  appointmentDateTime
  appointmentTime
  appointmentDate
  serviceDateTime
  serviceTime
  serviceDate
  consultationDateTime
  consultationTime
  consultationDate
  duration
  visit
  notes
  procedureType
  events {
    id
    type
    timestamp
    patientPin
    cancelReason
    channel
    content
    user {
      id
      name
    }
    text
    status
    exchangeId
  }
  organizationLocation {
    id
    name
  }
`;

const baseListFields = `
  id
  status
  appointmentDateTime
  appointmentTime
  appointmentDate
  serviceDateTime
  serviceTime
  serviceDate
  consultationDateTime
  consultationTime
  consultationDate
  duration
  visit
  notes
  procedureType
  events {
    id
    type
    timestamp
    patientPin
    cancelReason
    channel
    content
    text
    status
    exchangeId
  }
`;

const PatientFragment = gql`
  fragment SurgeonPatientData on Patient {
    id
    name
    homePhone
    cellPhone
    email
    dateOfBirth
    age
    physician {
      ...PhysicianBase
    }
  }
  ${physicianFragments.base}
`;

export const fragments = {
  all: gql`
    fragment SurgeonProcedureData on Procedure {
      ${baseFields}
      patient {
        ...SurgeonPatientData
      }
    }
    ${PatientFragment}
  `,
  withoutEvents: gql`
    fragment ProcedureDataWithoutEvents on Procedure {
      id
      status
      appointmentDateTime
      appointmentTime
      appointmentDate
      serviceDateTime
      serviceTime
      serviceDate
      consultationDateTime
      consultationTime
      consultationDate
      duration
      visit
      notes
      procedureType
      patient {
        ...SurgeonPatientData
      }
    }
    ${PatientFragment}
  `,
  base: gql`
    fragment SurgeonProcedureBase on Procedure {
     ${baseFields}
      scheduleRequest {
        id
        scheduleTransferDate
        scheduleTransferTime
        duration
        payload {
          forms
          signedByPhysician
        }
        state
        notesStatus
        toOrg {
          name
        }
        issues {
          ...ScheduleTransferIssueData
        }
      }
    }
    ${fragmentTransferIssue}
  `,
  questionnaire: gql`
    fragment SurgeonQuestionnaire on QuestionnaireAnswers {
      id
      progress
      questions
      answers
      submittedAt
    }
  `,
  list: gql`
    fragment SurgeonProcedureListData on Procedure {
      ${baseListFields}
      patient {
        ...SurgeonPatientData
      }
    }
    ${PatientFragment}
  `,
};

export const itemSubscription = gql`
  subscription procedure($id: Long!) {
    patient: procedure(id: $id) {
      ...SurgeonProcedureBase

      patient {
        ...SurgeonPatientData
      }
    }
  }
  ${fragments.base}
  ${PatientFragment}
`;

export const itemQuestionnaireSubscription = gql`
  subscription procedureQuestionnaire($id: Long!) {
    patient: procedure(id: $id) {
      entryQuestionnaire {
        ...SurgeonQuestionnaire
      }
    }
  }
  ${fragments.questionnaire}
`;

export const update = gql`
  mutation (
    $id: Long!
    $appointmentDate: Date
    $appointmentTime: String
    $procedureType: String
    $notes: String
    $patient: PatientInput
    $physician: Long
    $serviceDate: Date
    $serviceTime: String
    $duration: Int
    $consultationDate: Date
    $consultationTime: String
    $locationId: Long
  ) {
    editProcedure(
      id: $id
      notes: $notes
      appointmentDate: $appointmentDate
      appointmentTime: $appointmentTime
      consultationDate: $consultationDate
      consultationTime: $consultationTime
      procedureType: $procedureType
      patient: $patient
      physician: $physician
      serviceDate: $serviceDate
      serviceTime: $serviceTime
      duration: $duration
      locationId: $locationId
    ) {
      ...SurgeonProcedureData
    }
  }
  ${fragments.all}
`;

const create = gql`
  mutation (
    $patient: PatientInput!
    $physician: Long
    $consultationDate: Date
    $consultationTime: String
    $procedureType: String
    $locationId: Long
  ) {
    createProcedure(
      patient: $patient
      physician: $physician
      consultationDate: $consultationDate
      consultationTime: $consultationTime
      procedureType: $procedureType
      locationId: $locationId
    ) {
      ...SurgeonProcedureData
    }
  }
  ${fragments.all}
`;

export const sendRatingInvite = gql`
  mutation ($id: Long!) {
    sendRatingInvite(id: $id) {
      ...SurgeonProcedureData
    }
  }
  ${fragments.all}
`;

export const item = gql`
  query procedure($id: Long!) {
    patient: procedure(id: $id) {
      ...SurgeonProcedureData
    }
  }
  ${fragments.all}
`;

export const list = gql`
  query procedures($filter: SurgeonOfficeFilter) {
    patients: procedures(filter: $filter) {
      ...SurgeonProcedureData
      entryQuestionnaire {
        id
        progress
        submittedAt
      }
    }
  }
  ${fragments.list}
`;

export const listUnscheduled = gql`
  query unscheduledProcedures {
    procedures: unscheduledProcedures {
      id
      patientName
      patient {
        id
        name
        age
        sex
        physician {
          id
          hstId
          name
          email
        }
      }
      procedureType
      serviceDateTime
      serviceTime
      serviceDate
      duration
      scheduleRequest {
        id
      }
    }
  }
`;

export const getProcedure = gql`
  query procedure($id: Long!) {
    procedure(id: $id) {
      id
      patientName
      patient {
        id
        name
        age
        sex
        physician {
          id
          hstId
          name
          email
        }
      }
      procedureType
      serviceDateTime
      serviceTime
      serviceDate
      duration
      scheduleRequest {
        id
      }
    }
  }
`;

export const listSubscription = gql`
  subscription procedures($filter: SurgeonOfficeFilter) {
    patients: procedures(filter: $filter) {
      ...SurgeonProcedureData
      entryQuestionnaire {
        id
        progress
        submittedAt
      }
    }
  }
  ${fragments.list}
`;

export const procedureStatistics = gql`
  subscription procedureStatistics($filter: SurgeonOfficeFilter) {
    procedureStatistics(filter: $filter) {
      questionnaireSent
      questionnaireCompleted
      toBeScheduled
      surgeryScheduled
      missingInformation
    }
  }
`;

export default {
  item,
  list,
  listSubscription,
  update,
  create,
  itemSubscription,
  sendRatingInvite,
};
